import useData from './hooks/useData';

import BatteryCardGrid from './components/BatteryCardGrid';
import CartTray from './components/CartTray';
import DroneCardGrid from './components/DroneCardGrid';
import FilterBar from './components/FilterBar';
import KpiStrip from './components/KpiStrip';
import MaintenanceDrawers from './components/MaintenanceDrawers';
import MaintenancePageHeader from './components/MaintenancePageHeader';
import MaintenancePanels from './components/MaintenancePanels';
import RecommendedPartsSection from './components/RecommendedPartsSection';
import ViewToggle from './components/ViewToggle';
import { DEFAULT_PHASE, PHASE_HEALTH_FAULTS, PHASE_ORDERING, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const MaintenancePage: FunctionComponent<Props> = ({ phase = DEFAULT_PHASE, testId = TEST_ID }) => {
    const data = useData({ phase });
    const showRecommendedParts = phase >= PHASE_HEALTH_FAULTS;
    const showCart = phase >= PHASE_ORDERING;
    const isBatteriesView = data.activeView === 'batteries';

    return (
        <div className={styles.maintenancePage} data-testid={testId}>
            <MaintenancePageHeader onConfigureIntervals={data.handleOpenIntervalConfig} />

            <KpiStrip phase={phase} />

            <ViewToggle activeView={data.activeView} onViewChange={data.setActiveView} />

            {!isBatteriesView && (
                <FilterBar
                    activeFilter={data.activeFilter}
                    filterKeys={data.visibleFilterKeys}
                    onFilterChange={data.setActiveFilter}
                    onSearchChange={data.setSearchQuery}
                    searchQuery={data.searchQuery}
                />
            )}

            {isBatteriesView ? (
                <BatteryCardGrid
                    batteries={data.batteries}
                    cycleInterval={data.batteryCycleInterval}
                    droneNames={data.droneNames}
                    onOpenBattery={data.handleOpenBatteryDrawer}
                />
            ) : (
                <DroneCardGrid
                    drones={data.visibleDrones}
                    isAdmin={data.isAdmin}
                    onOpenDrone={data.handleOpenDrawer}
                    onSetAirworthy={data.setAirworthy}
                    onSetLifecycle={data.setLifecycle}
                    phase={phase}
                    serviceIntervals={data.serviceIntervals}
                />
            )}

            {!isBatteriesView && showRecommendedParts && (
                <RecommendedPartsSection
                    droneNames={data.droneNames}
                    onAdd={data.handleAddToCart}
                    phase={phase}
                    recommendations={data.recommendations}
                />
            )}

            {showCart && (
                <CartTray
                    itemCount={data.cartItemCount}
                    onOpen={data.handleOpenCart}
                    subtotal={data.cartSubtotal}
                />
            )}

            <MaintenanceDrawers data={data} phase={phase} />

            <MaintenancePanels data={data} showCart={showCart} />
        </div>
    );
};

export default MaintenancePage;
